import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { equipamentoSchema, TIPOS_EQUIPAMENTO, type EquipamentoFormData } from './schema'
import { useClientesV2 } from '../../hooks/useEmpresas'
import styles from '../_shared/CrudForm.module.css'

interface EquipamentoFormProps {
  defaultValues?: Partial<EquipamentoFormData>
  onSubmit: (data: EquipamentoFormData) => void
  isLoading?: boolean
  submitLabel?: string
}

export default function EquipamentoForm({
  defaultValues,
  onSubmit,
  isLoading,
  submitLabel = 'Salvar',
}: EquipamentoFormProps) {
  const { data: empresasData, isLoading: loadingEmpresas } = useClientesV2()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EquipamentoFormData>({
    resolver: zodResolver(equipamentoSchema),
    defaultValues: {
      empresa: '',
      tipo: 'split_hi_wall',
      marca: '',
      modelo: '',
      numero_serie: '',
      capacidade_btu: undefined,
      observacoes: '',
      ...defaultValues,
    },
  })

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data))} className={styles.form} noValidate>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Identificação</h2>
        <div className={styles.grid2}>
          <div className={styles.field}>
            <label htmlFor="empresa">Empresa-Cliente *</label>
            <select
              id="empresa"
              {...register('empresa')}
              aria-invalid={!!errors.empresa}
              disabled={loadingEmpresas}
            >
              <option value="">{loadingEmpresas ? 'Carregando...' : '— selecione —'}</option>
              {empresasData?.results.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.razao_social} ({e.cnpj})
                </option>
              ))}
            </select>
            {errors.empresa && (
              <span role="alert" className={styles.error}>
                {errors.empresa.message}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="tipo">Tipo de equipamento *</label>
            <select id="tipo" {...register('tipo')} aria-invalid={!!errors.tipo}>
              {TIPOS_EQUIPAMENTO.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {errors.tipo && (
              <span role="alert" className={styles.error}>
                {errors.tipo.message}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="marca">Marca *</label>
            <input id="marca" {...register('marca')} aria-invalid={!!errors.marca} />
            {errors.marca && (
              <span role="alert" className={styles.error}>
                {errors.marca.message}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="modelo">Modelo *</label>
            <input id="modelo" {...register('modelo')} aria-invalid={!!errors.modelo} />
            {errors.modelo && (
              <span role="alert" className={styles.error}>
                {errors.modelo.message}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="numero_serie">Número de série</label>
            <input id="numero_serie" {...register('numero_serie')} />
          </div>

          <div className={styles.field}>
            <label htmlFor="capacidade_btu">Capacidade (BTU)</label>
            <input
              id="capacidade_btu"
              type="number"
              {...register('capacidade_btu')}
              placeholder="ex: 18000"
              aria-invalid={!!errors.capacidade_btu}
            />
            {errors.capacidade_btu && (
              <span role="alert" className={styles.error}>
                {errors.capacidade_btu.message}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Observações</h2>
        <div className={styles.field}>
          <textarea
            id="observacoes"
            {...register('observacoes')}
            rows={4}
            className={styles.textarea}
          />
        </div>
      </section>

      <div className={styles.actions}>
        <button type="submit" className={styles.btnPrimary} disabled={isLoading}>
          {isLoading ? 'Salvando...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
